import React, { useState, useEffect } from "react";
import JsonView from '@uiw/react-json-view';
import ApiClient from "../api/ApiClient";

const pageSizeOptions = [10, 25, 100, 500];

const orderByOptions = [
  { value: "IdDesc", label: "ID (Desc)" },
  { value: "Id", label: "ID (Asc)" },
  { value: "StartTimeDesc", label: "Start Time (Desc)" },
  { value: "StartTime", label: "Start Time (Asc)" },
  { value: "EndTimeDesc", label: "End Time (Desc)" },
  { value: "EndTime", label: "End Time (Asc)" },
];

// Helper function to get default date range (last 5 years)
function getDefaultDateRange() {
  const now = new Date();
  const fiveYearsAgo = new Date();
  fiveYearsAgo.setFullYear(now.getFullYear() - 5);
  return {
    from: fiveYearsAgo,
    to: now
  };
}

// Helper function to format date for HTML date input (YYYY-MM-DD)
function formatDateForInput(date) {
  if (!date) return "";
  const d = new Date(date);
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}

// Helper function to parse date from HTML date input
function parseDateFromInput(dateString) {
  if (!dateString) return null;
  return new Date(dateString + 'T00:00:00');
}

export const SimulationReports = () => {
  const [decisionMakerId, setDecisionMakerId] = useState("");
  const [orderBy, setOrderBy] = useState("IdDesc");

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0); // zero-based

  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state for viewing details
  const [selectedReport, setSelectedReport] = useState(null);
  const [showEntries, setShowEntries] = useState(false);

  // Pagination state for trades and notifications in modal
  const [tradesPage, setTradesPage] = useState(0);
  const [tradesPageSize] = useState(25);
  const [tradesTotalCount, setTradesTotalCount] = useState(0);
  const [tradesData, setTradesData] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);

  const [notificationsPage, setNotificationsPage] = useState(0);
  const [notificationsPageSize] = useState(25);
  const [notificationsTotalCount, setNotificationsTotalCount] = useState(0);
  const [notificationsData, setNotificationsData] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Trigger dialog state
  const [showTriggerDialog, setShowTriggerDialog] = useState(false);
  const [decisionMakers, setDecisionMakers] = useState([]);
  const [simulationForm, setSimulationForm] = useState(() => {
    const defaultDates = getDefaultDateRange();
    return {
      decisionMaker: "",
      options: {},
      dataToFeed: [{
        tickersFilter: { CollectCandles: true },
        dateRange: {
          startDate: defaultDates.from,
          endDate: defaultDates.to,
          key: 'selection'
        }
      }]
    };
  });
  const [submittingSimulation, setSubmittingSimulation] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    if (showTriggerDialog) {
      fetchDecisionMakers();
    }
  }, [showTriggerDialog]);

  useEffect(() => {
    if (selectedReport) {
      fetchTrades(selectedReport.Id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, tradesPage, tradesPageSize]);

  useEffect(() => {
    if (selectedReport) {
      fetchNotifications(selectedReport.Id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, notificationsPage, notificationsPageSize]);

  async function fetchData() {
    setLoading(true);
    const body = {
      DecisionMakerId: decisionMakerId || undefined,
      OrderBy: orderBy,
      Limit: pageSize,
      Offset: page * pageSize,
    };

    try {
      const data = await ApiClient.searchSimulationReports(body);
      setItems(data.Items);
      setTotal(data.TotalCount);
    } catch (err) {
      console.error("Search failed", err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDecisionMakers() {
    try {
      const data = await ApiClient.getDecisionMakers();
      setDecisionMakers(data);
    } catch (err) {
      console.error("Failed to fetch decision makers", err);
      setDecisionMakers([]);
    }
  }

  async function fetchTrades(reportId) {
    setTradesLoading(true);
    try {
      const data = await ApiClient.getSimulationReportTrades(
        reportId,
        tradesPage * tradesPageSize,
        tradesPageSize
      );
      setTradesData(data.Items || []);
      setTradesTotalCount(data.TotalCount || 0);
    } catch (err) {
      console.error("Failed to fetch trades", err);
      setTradesData([]);
      setTradesTotalCount(0);
    } finally {
      setTradesLoading(false);
    }
  }

  async function fetchNotifications(reportId) {
    setNotificationsLoading(true);
    try {
      const data = await ApiClient.getSimulationReportNotifications(
        reportId,
        notificationsPage * notificationsPageSize,
        notificationsPageSize
      );
      setNotificationsData(data.Items || []);
      setNotificationsTotalCount(data.TotalCount || 0);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
      setNotificationsData([]);
      setNotificationsTotalCount(0);
    } finally {
      setNotificationsLoading(false);
    }
  }

  function handleDecisionMakerChange(dmId) {
    const dm = decisionMakers.find(d => d.Id === dmId);
    setSimulationForm(prev => ({
      ...prev,
      decisionMaker: dmId,
      options: dm?.DefaultOptions || {}
    }));
  }

  function handleOptionsChange(field, value) {
    setSimulationForm(prev => ({
      ...prev,
      options: {
        ...prev.options,
        [field]: value
      }
    }));
  }

  function handleDataToFeedChange(index, field, value) {
    setSimulationForm(prev => ({
      ...prev,
      dataToFeed: prev.dataToFeed.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  }

  function handleStartDateChange(index, dateString) {
    const startDate = parseDateFromInput(dateString);
    setSimulationForm(prev => ({
      ...prev,
      dataToFeed: prev.dataToFeed.map((item, i) => {
        if (i === index) {
          const currentEndDate = item.dateRange.endDate;
          // If start date is after end date, adjust end date to match start date
          const adjustedEndDate = startDate && currentEndDate && startDate > currentEndDate
            ? startDate
            : currentEndDate;

          return {
            ...item,
            dateRange: {
              ...item.dateRange,
              startDate: startDate,
              endDate: adjustedEndDate,
              key: 'selection'
            }
          };
        }
        return item;
      })
    }));
  }

  function handleEndDateChange(index, dateString) {
    const endDate = parseDateFromInput(dateString);
    setSimulationForm(prev => ({
      ...prev,
      dataToFeed: prev.dataToFeed.map((item, i) => {
        if (i === index) {
          const currentStartDate = item.dateRange.startDate;
          // If end date is before start date, adjust start date to match end date
          const adjustedStartDate = endDate && currentStartDate && endDate < currentStartDate
            ? endDate
            : currentStartDate;

          return {
            ...item,
            dateRange: {
              ...item.dateRange,
              startDate: adjustedStartDate,
              endDate: endDate,
              key: 'selection'
            }
          };
        }
        return item;
      })
    }));
  }  function addDataToFeedEntry() {
    const defaultDates = getDefaultDateRange();
    setSimulationForm(prev => ({
      ...prev,
      dataToFeed: [...prev.dataToFeed, {
        tickersFilter: { CollectCandles: true },
        dateRange: {
          startDate: defaultDates.from,
          endDate: defaultDates.to,
          key: 'selection'
        }
      }]
    }));
  }

  function removeDataToFeedEntry(index) {
    setSimulationForm(prev => ({
      ...prev,
      dataToFeed: prev.dataToFeed.filter((_, i) => i !== index)
    }));
  }

  async function handleSimulationSubmit(e) {
    e.preventDefault();
    setSubmittingSimulation(true);

    try {
      const body = {
        DecisionMaker: simulationForm.decisionMaker,
        Options: simulationForm.options,
        DataToFeed: simulationForm.dataToFeed.map(entry => ({
          TickersFilter: entry.tickersFilter,
          From: entry.dateRange.startDate.toISOString(),
          To: entry.dateRange.endDate.toISOString()
        }))
      };

      const success = await ApiClient.triggerSimulation(body);
      if (success) {
        alert("Simulation started successfully!");
        setShowTriggerDialog(false);
        // Reset form
        const defaultDates = getDefaultDateRange();
        setSimulationForm({
          decisionMaker: "",
          options: {},
          dataToFeed: [{
            tickersFilter: { CollectCandles: true },
            dateRange: {
              startDate: defaultDates.from,
              endDate: defaultDates.to,
              key: 'selection'
            }
          }]
        });
        // Refresh the list
        fetchData();
      }
    } catch (err) {
      console.error("Failed to start simulation", err);
      alert(`Failed to start simulation: ${err.message}`);
    } finally {
      setSubmittingSimulation(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setPage(0);
    fetchData();
  }

  async function handleDeleteReport(report) {
    const confirmed = window.confirm(
      `Delete simulation report #${report.Id}? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await ApiClient.deleteSimulationReport(report.Id);
      if (selectedReport && selectedReport.Id === report.Id) {
        setSelectedReport(null);
        setShowEntries(false);
        setTradesPage(0);
        setNotificationsPage(0);
        setTradesData([]);
        setNotificationsData([]);
      }
      await fetchData();
    } catch (err) {
      console.error("Failed to delete simulation report", err);
      alert(`Failed to delete simulation report: ${err.message}`);
    }
  }

  function renderTable() {
    if (loading)
      return (
        <p>
          <em>Loading...</em>
        </p>
      );
    if (!items || items.length === 0) return <p>No results</p>;

    return (
      <>
        <table className="table table-striped">
          <thead>
            <tr>
              <th>ID</th>
              <th>Decision Maker ID</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Duration</th>
              <th>Trades</th>
              <th>Notifications</th>
              <th>Total Profit</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((report) => {
              return (
                <tr key={report.Id}>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedReport(report)}
                  >
                    {report.Id}
                  </td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedReport(report)}
                  >
                    {report.DecisionMakerId}
                  </td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedReport(report)}
                  >
                    {report.getStartTime()}
                  </td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedReport(report)}
                  >
                    {report.getEndTime()}
                  </td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedReport(report)}
                  >
                    {report.getDuration()}
                  </td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedReport(report)}
                  >
                    {report.getTradesCount()}
                  </td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedReport(report)}
                  >
                    {report.getNotificationsCount()}
                  </td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedReport(report)}
                  >
                    <span className={report.getProfitBadgeClass()}>
                      {report.getFormattedProfit()}
                    </span>
                  </td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedReport(report)}
                  >
                    <span className={report.getStatusBadgeClass()}>
                      {report.getStatus()}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary mr-1"
                      onClick={() => window.open(`/simulation-reports/${report.Id}/chart-data`, '_blank')}
                      title="View trades and notifications on candle charts"
                    >
                      View on charts
                    </button>
                    &nbsp;
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteReport(report)}
                      title="Delete simulation report"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Details modal */}
        {selectedReport && (
          <div
            className="modal show"
            tabIndex="-1"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Simulation Report #{selectedReport.Id}
                  </h5>
                </div>
                <div className="modal-body">
                    <div className="row">
                    <div className="col-md-6">
                      <strong>Decision Maker ID:</strong> {selectedReport.DecisionMakerId}
                    </div>
                    <div className="col-md-6">
                      <strong>Duration:</strong> {selectedReport.getDuration()}
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-md-6">
                      <strong>Start Time:</strong> {selectedReport.getStartTime()}
                    </div>
                    <div className="col-md-6">
                      <strong>End Time:</strong> {selectedReport.getEndTime()}
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-md-6">
                      <strong>Total Profit:</strong>
                      <span className={`ml-2 ${selectedReport.getProfitBadgeClass()}`}>
                        {selectedReport.getFormattedProfit()}
                      </span>
                    </div>
                    <div className="col-md-6">
                      <strong>Status:</strong>
                      <span className={`ml-2 ${selectedReport.getStatusBadgeClass()}`}>
                        {selectedReport.getStatus()}
                      </span>
                    </div>
                  </div>                  {selectedReport.Error && (
                    <div className="mt-3">
                      <strong>Error:</strong>
                      <div className="alert alert-danger mt-1">
                        {selectedReport.Error}
                      </div>
                    </div>
                  )}

                  {selectedReport.Options && (
                    <div className="mt-3">
                      <strong>Options:</strong>
                      <pre className="bg-light p-2 mt-1" style={{ fontSize: "12px", maxHeight: "200px", overflow: "auto" }}>
                        {JSON.stringify(selectedReport.Options, null, 2)}
                      </pre>
                    </div>
                  )}

                  {(selectedReport.TradesCount > 0 || tradesData.length > 0) && (
                    <div className="mt-3">
                      <strong>Trades ({tradesTotalCount}):</strong>
                      {tradesLoading ? (
                        <div className="text-center mt-2">
                          <em>Loading trades...</em>
                        </div>
                      ) : (
                        <>
                          <div className="table-responsive" style={{ maxHeight: "200px", overflow: "auto" }}>
                            <table className="table table-sm table-striped mt-1">
                              <thead>
                                <tr>
                                  <th>Type</th>
                                  <th>Ticker</th>
                                  <th>Quantity</th>
                                  <th>Price</th>
                                  <th>Total</th>
                                  <th>Commission</th>
                                  <th>Time</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tradesData.map((trade, idx) => (
                                  <tr key={idx}>
                                    <td>{trade.Type}</td>
                                    <td>{trade.TickerId}</td>
                                    <td>{trade.Quantity}</td>
                                    <td>${trade.ActualPrice?.toFixed(2)}</td>
                                    <td>${trade.TotalAmount?.toFixed(2)}</td>
                                    <td>${trade.Comission?.toFixed(2) || '0.00'}</td>
                                    <td>{new Date(trade.Time).toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {tradesTotalCount > tradesPageSize && (
                            <div className="d-flex justify-content-between align-items-center mt-2">
                              <div>
                                Page {tradesPage + 1} of {Math.ceil(tradesTotalCount / tradesPageSize)}
                              </div>
                              <div className="btn-group">
                                <button
                                  className="btn btn-sm btn-secondary"
                                  disabled={tradesPage === 0}
                                  onClick={() => setTradesPage(tradesPage - 1)}
                                >
                                  Previous
                                </button>
                                <button
                                  className="btn btn-sm btn-secondary"
                                  disabled={(tradesPage + 1) * tradesPageSize >= tradesTotalCount}
                                  onClick={() => setTradesPage(tradesPage + 1)}
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {(selectedReport.NotificationsCount > 0 || notificationsData.length > 0) && (
                    <div className="mt-3">
                      <strong>Notifications ({notificationsTotalCount}):</strong>
                      {notificationsLoading ? (
                        <div className="text-center mt-2">
                          <em>Loading notifications...</em>
                        </div>
                      ) : (
                        <>
                          <div className="table-responsive" style={{ maxHeight: "200px", overflow: "auto" }}>
                            <table className="table table-sm table-striped mt-1">
                              <thead>
                                <tr>
                                  <th>Time</th>
                                  <th>Type</th>
                                  <th>Ticker</th>
                                  <th>Candle Time</th>
                                  <th>Message</th>
                                  <th>Triggered Candle</th>
                                </tr>
                              </thead>
                              <tbody>
                                {notificationsData.map((notification, idx) => (
                                  <tr key={idx}>
                                    <td>{new Date(notification.Time).toLocaleString()}</td>
                                    <td>{notification.Type}</td>
                                    <td>{notification.TickerId || 'N/A'}</td>
                                    <td>{notification.TriggeredCandleTime ? new Date(notification.TriggeredCandleTime).toLocaleString() : 'N/A'}</td>
                                    <td>{notification.Message || 'N/A'}</td>
                                    <td>#{notification.TriggeredCandleId}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {notificationsTotalCount > notificationsPageSize && (
                            <div className="d-flex justify-content-between align-items-center mt-2">
                              <div>
                                Page {notificationsPage + 1} of {Math.ceil(notificationsTotalCount / notificationsPageSize)}
                              </div>
                              <div className="btn-group">
                                <button
                                  className="btn btn-sm btn-secondary"
                                  disabled={notificationsPage === 0}
                                  onClick={() => setNotificationsPage(notificationsPage - 1)}
                                >
                                  Previous
                                </button>
                                <button
                                  className="btn btn-sm btn-secondary"
                                  disabled={(notificationsPage + 1) * notificationsPageSize >= notificationsTotalCount}
                                  onClick={() => setNotificationsPage(notificationsPage + 1)}
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {showEntries && selectedReport.Entries && selectedReport.Entries.length > 0 && (
                    <div className="mt-3">
                      <strong>Entries ({selectedReport.Entries.length}):</strong>
                      <div className="mt-2" style={{ maxHeight: "400px", overflow: "auto", border: "1px solid #ddd", borderRadius: "4px", padding: "10px", backgroundColor: "#f8f9fa" }}>
                        <JsonView
                          value={selectedReport.Entries}
                          collapsed={1}
                          displayDataTypes={false}
                          enableClipboard={true}
                          style={{
                            '--w-rjv-font-family': 'Monaco, "Lucida Console", monospace',
                            '--w-rjv-font-size': '13px',
                            '--w-rjv-line-height': '1.4',
                            '--w-rjv-color': '#333',
                            '--w-rjv-key-string': '#0366d6',
                            '--w-rjv-background-color': 'transparent'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {showEntries && (!selectedReport.Entries || selectedReport.Entries.length === 0) && (
                    <div className="mt-3">
                      <strong>Entries:</strong>
                      <div className="alert alert-info mt-1">
                        No entries available for this simulation report.
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-info"
                    onClick={() => setShowEntries(!showEntries)}
                  >
                    {showEntries ? 'Hide Entries' : 'Show Entries'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedReport(null);
                      setShowEntries(false);
                      setTradesPage(0);
                      setNotificationsPage(0);
                      setTradesData([]);
                      setNotificationsData([]);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <div>
        <h1>Simulation Reports</h1>
      <form onSubmit={onSearch} className="mb-3">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="row">
            <div className="col-md-6 mb-2">
              <label className="col-form-label">Decision Maker ID</label>
              <input
                placeholder="Decision Maker ID"
                className="form-control"
                value={decisionMakerId}
                onChange={(e) => setDecisionMakerId(e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-2">
              <label className="col-form-label">Order By</label>
              <select
                className="form-control"
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value)}
              >
                {orderByOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 mb-2 d-flex align-items-end" style={{ gap: "10px" }}>
              <button className="btn btn-primary" type="submit">
                Search
              </button>
              <button
                className="btn btn-success"
                type="button"
                onClick={() => setShowTriggerDialog(true)}
              >
                New Simulation
              </button>
            </div>
          </div>
        </div>
      </form>

      <div className="mb-2 d-flex align-items-center">
        <div className="mr-3">Page size:</div>
        <select
          className="form-control mr-3"
          style={{ width: "120px" }}
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
        >
          {pageSizeOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div>
          Page: {page + 1} / {totalPages || 1}
        </div>
      </div>

      <div className="mb-3 d-flex" style={{ gap: "8px", alignItems: "center" }}>
        <button
          className="btn btn-secondary mr-2"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
      </div>

      {renderTable()}

            </div>

      <div className="mt-2 d-flex" style={{ gap: "8px" }}>
        <button
          className="btn btn-secondary mr-2"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
      </div>

      {/* New Simulation Dialog */}
      {showTriggerDialog && (
        <div
          className="modal show"
          tabIndex="-1"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Start New Simulation</h5>
                <button
                  type="button"
                  className="close"
                  aria-label="Close"
                  onClick={() => setShowTriggerDialog(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <form onSubmit={handleSimulationSubmit}>
                <div className="modal-body">
                  {/* Decision Maker Selection */}
                  <div className="form-group mb-3">
                    <label className="form-label">Decision Maker *</label>
                    <select
                      className="form-control"
                      value={simulationForm.decisionMaker}
                      onChange={(e) => handleDecisionMakerChange(e.target.value)}
                      required
                    >
                      <option value="">Select a Decision Maker</option>
                      {decisionMakers.map(dm => (
                        <option key={dm.Id} value={dm.Id}>
                          {dm.Name} ({dm.Id})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Options Configuration */}
                  {simulationForm.decisionMaker && (
                    <div className="form-group mb-3">
                      <label className="form-label">Options</label>
                      <div className="card">
                        <div className="card-body">
                          {Object.entries(simulationForm.options).map(([key, value]) => (
                            <div key={key} className="form-group mb-2">
                              <label className="form-label">{key}</label>
                              {typeof value === 'number' ? (
                                <input
                                  type="number"
                                  step="any"
                                  className="form-control"
                                  value={value}
                                  onChange={(e) => handleOptionsChange(key, parseFloat(e.target.value) || 0)}
                                />
                              ) : typeof value === 'boolean' ? (
                                <select
                                  className="form-control"
                                  value={value.toString()}
                                  onChange={(e) => handleOptionsChange(key, e.target.value === 'true')}
                                >
                                  <option value="false">False</option>
                                  <option value="true">True</option>
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={value}
                                  onChange={(e) => handleOptionsChange(key, e.target.value)}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Data to Feed Configuration */}
                  <div className="form-group mb-3">
                    <label className="form-label">Data to Feed *</label>
                    {simulationForm.dataToFeed.map((entry, index) => (
                      <div key={index} className="card mb-2">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Entry #{index + 1}</h6>
                            {simulationForm.dataToFeed.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => removeDataToFeedEntry(index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>

                          <div className="row">
                            <div className="col-md-12">
                              <label className="form-label">Date Range *</label>
                              <div className="row">
                                <div className="col-md-6">
                                  <label className="form-label small">Start Date</label>
                                  <input
                                    type="date"
                                    className="form-control"
                                    value={formatDateForInput(entry.dateRange.startDate)}
                                    max={formatDateForInput(entry.dateRange.endDate)}
                                    onChange={(e) => handleStartDateChange(index, e.target.value)}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label small">End Date</label>
                                  <input
                                    type="date"
                                    className="form-control"
                                    value={formatDateForInput(entry.dateRange.endDate)}
                                    min={formatDateForInput(entry.dateRange.startDate)}
                                    onChange={(e) => handleEndDateChange(index, e.target.value)}
                                  />
                                </div>
                              </div>
                              <small className="text-muted">
                                Selected: {entry.dateRange.startDate?.toDateString()} - {entry.dateRange.endDate?.toDateString()}
                              </small>
                            </div>
                          </div>

                          <div className="mt-2">
                            <label className="form-label">Tickers Filter</label>
                            <div className="row">
                              <div className="col-md-4">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Code (e.g., AAPL)"
                                  value={entry.tickersFilter.Code || ""}
                                  onChange={(e) => handleDataToFeedChange(index, 'tickersFilter', {
                                    ...entry.tickersFilter,
                                    Code: e.target.value
                                  })}
                                />
                              </div>
                              <div className="col-md-4">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Tag"
                                  value={entry.tickersFilter.Tag || ""}
                                  onChange={(e) => handleDataToFeedChange(index, 'tickersFilter', {
                                    ...entry.tickersFilter,
                                    Tag: e.target.value
                                  })}
                                />
                              </div>
                              <div className="col-md-4">
                                <select
                                  className="form-control"
                                  value={entry.tickersFilter.CollectCandles === undefined ? "" : entry.tickersFilter.CollectCandles.toString()}
                                  onChange={(e) => handleDataToFeedChange(index, 'tickersFilter', {
                                    ...entry.tickersFilter,
                                    CollectCandles: e.target.value === "" ? undefined : e.target.value === "true"
                                  })}
                                >
                                  <option value="">Collect candles - Any</option>
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={addDataToFeedEntry}
                    >
                      Add Another Entry
                    </button>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowTriggerDialog(false)}
                    disabled={submittingSimulation}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={submittingSimulation || !simulationForm.decisionMaker}
                  >
                    {submittingSimulation ? "Starting..." : "Start Simulation"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SimulationReports;